import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { BasicTextField } from 'src/components/shared/form/BasicTextField'
import {
  INVOICE_CANCEL_CONFIRMATION_WORD,
  invoiceActionConsequences,
} from 'src/constants/invoice/constants'
import { InvoiceActionVerb } from 'src/constants/invoice/enums'

/** Only names the field for `BasicTextField`'s generic; the value is plain local state. */
interface TypedConfirmationFields {
  typedConfirmation: string
}

interface Props {
  verb: InvoiceActionVerb
  composedInvoiceLabel: string
  invoiceCount: number
  typedConfirmation: string
  onTypedConfirmationChange(value: string): void
}

const InvoiceActionConfirmStepComponent = ({
  verb,
  composedInvoiceLabel,
  invoiceCount,
  typedConfirmation,
  onTypedConfirmationChange,
}: Props) => {
  // Cancel is the highest-impact verb and the only one behind a typed guard. Adding the guard
  // to every verb would train admins to type through it.
  const requiresTypedConfirmation = verb === InvoiceActionVerb.Cancel

  const invoiceCountText =
    invoiceCount === 1 ? '1 partner invoice' : `all ${invoiceCount} partner invoices`

  return (
    <div css={tw`flex flex-col gap-4`}>
      <Typography variant="body2">
        {invoiceActionConsequences[verb].replace(
          '{count}',
          `${invoiceCountText} in ${composedInvoiceLabel}`,
        )}
      </Typography>
      <div
        css={tw`flex flex-col gap-2 rounded-lg border border-solid border-shades-100 bg-shades-50 p-4`}
      >
        <div css={tw`flex flex-row justify-between`}>
          <Typography variant="body2" css={tw`text-shades-500`}>
            Invoices affected
          </Typography>
          <Typography variant="subtitle2">{invoiceCount}</Typography>
        </div>
        <div css={tw`flex flex-row justify-between`}>
          <Typography variant="body2" css={tw`text-shades-500`}>
            This action
          </Typography>
          <Typography variant="subtitle2">Cannot be undone</Typography>
        </div>
        {/* Each invoice is a separate request, so a partial failure is a real outcome. Saying
            so up front is the honest framing — the results step then reports what happened. */}
        {invoiceCount > 1 ? (
          <Typography variant="body2" css={tw`text-shades-500`}>
            Each invoice is updated separately. If one fails, the rest still run and you will see
            exactly which ones changed.
          </Typography>
        ) : null}
      </div>
      {requiresTypedConfirmation ? (
        <BasicTextField<TypedConfirmationFields>
          name="typedConfirmation"
          label={`Type ${INVOICE_CANCEL_CONFIRMATION_WORD} to confirm`}
          placeholder={INVOICE_CANCEL_CONFIRMATION_WORD}
          value={typedConfirmation}
          autoComplete="off"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onTypedConfirmationChange(event.target.value)
          }}
          inputProps={{ 'data-testid': 'invoice-action-type-to-confirm' }}
        />
      ) : null}
    </div>
  )
}

export const InvoiceActionConfirmStep = memo(InvoiceActionConfirmStepComponent)
